/**
 * Leases and Agreements Routes Configuration
 */

import { MODULE_HEADER_BG } from '@/theme/moduleHeader';
import React, { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { ModuleBannerProvider, type ModuleBanner } from '../../../../core/layout/moduleBanner';

// Setup and Reports draw the slim module masthead in the LAMS slate -> indigo -> violet.
// Reports draw the LAMS UDA picker (the scope the report data obeys); Setup stays
// unscoped on purpose (see LamsUdaScope), so it gets no picker.
const LAMS_BANNER_BG = MODULE_HEADER_BG;
const LAMS_SETUP_BANNER: ModuleBanner = { background: LAMS_BANNER_BG, title: 'Setup' };
const LAMS_REPORTS_BANNER: ModuleBanner = { background: LAMS_BANNER_BG, title: 'Reports' };

// Side-effect import: installs the axios interceptor that stamps every LAMS request
// made from the dashboard / agreements list with the UDA picked in that screen's own
// dropdown. Imported here rather than in a page so the header is attached on a hard
// reload straight onto the dashboard, before any of those pages has rendered.
import '../components/LamsUdaScope';
import LamsViewGate from '../components/LamsViewGate';

const LeasesAgreementsDashboard = lazy(() => import('../pages/LeasesAgreementsDashboard'));
const AgreementsList = lazy(() => import('../pages/AgreementsList'));
const ServiceRequests = lazy(() => import('../pages/ServiceRequests'));
const DefaulterManagement = lazy(() => import('../pages/DefaulterManagement'));
const NoticeManagement = lazy(() => import('../pages/NoticeManagement'));
const DcbManagement = lazy(() => import('../pages/DcbManagement'));
const CancellationManagement = lazy(() => import('../pages/CancellationManagement'));
const RenewalManagement = lazy(() => import('../pages/RenewalManagement'));
const DocumentManagement = lazy(() => import('../pages/DocumentManagement'));
const NotificationLog = lazy(() => import('../pages/NotificationLog'));
const ConfigurationManagement = lazy(() => import('../pages/ConfigurationManagement'));
const JudgementManagement = lazy(() => import('../pages/JudgementManagement'));
const ObjectionManagement = lazy(() => import('../pages/ObjectionManagement'));
const RemissionManagement = lazy(() => import('../pages/RemissionManagement'));
const ChallanManagement = lazy(() => import('../pages/ChallanManagement'));
const DataEntryManagement = lazy(() => import('../pages/DataEntryManagement'));

// Spec DTCP-ERP-2025-001 §4.4 screens.
const TransferManagement = lazy(() => import('../pages/TransferManagement'));
const DisputeManagement = lazy(() => import('../pages/DisputeManagement'));
const LeaseCalendar = lazy(() => import('../pages/LeaseCalendar'));
const AgreementTypeManagement = lazy(() => import('../masters/agreement-type'));
const AgreementTemplateManagement = lazy(() => import('../masters/agreement-template'));

// Import routes from core modules
import { reportsRoutes } from '../../../../core/reports/routes';
import { setupRoutes } from '../../../../core/setup/routes';

// Simple pass-through layout for nested routes
const PassThroughLayout = () => <Outlet />;

export const leasesAgreementsRoutes: RouteObject[] = [
  {
    index: true,
    element: <LamsViewGate view="dslams_view_dashboard"><LeasesAgreementsDashboard /></LamsViewGate>
  },
  {
    path: 'list',
    element: <LamsViewGate view="dslams_view_agreements"><AgreementsList /></LamsViewGate>
  },
  {
    path: 'agreements',
    element: <LamsViewGate view="dslams_view_agreements"><AgreementsList /></LamsViewGate>
  },
  {
    path: 'service-requests',
    element: <ServiceRequests />
  },
  {
    path: 'defaulters',
    element: <DefaulterManagement />
  },
  {
    path: 'notices',
    element: <NoticeManagement />
  },
  {
    path: 'dcb',
    element: <DcbManagement />
  },
  {
    path: 'cancellations',
    element: <LamsViewGate view="dslams_view_cancellations"><CancellationManagement /></LamsViewGate>
  },
  {
    path: 'renewals',
    element: <LamsViewGate view="dslams_view_renewals"><RenewalManagement /></LamsViewGate>
  },
  {
    path: 'documents',
    element: <DocumentManagement />
  },
  {
    path: 'notifications',
    element: <NotificationLog />
  },
  {
    /*
     * The same screen under the name the module's own links and the sidebar entry use.
     * Both paths are served rather than one redirecting to the other: 'notifications' is
     * what the existing menu row and every bookmark point at, and 'lamsnotifications' is
     * what the module was asked for. A rename would have broken the first silently.
     */
    path: 'lamsnotifications',
    element: <NotificationLog />
  },
  {
    path: 'configuration',
    element: <ConfigurationManagement />
  },
  {
    path: 'judgements',
    element: <LamsViewGate view="dslams_view_judgements"><JudgementManagement /></LamsViewGate>
  },
  {
    path: 'objections',
    element: <LamsViewGate view="dslams_view_objections"><ObjectionManagement /></LamsViewGate>
  },
  {
    path: 'remissions',
    element: <LamsViewGate view="dslams_view_remissions"><RemissionManagement /></LamsViewGate>
  },
  {
    path: 'challans',
    element: <ChallanManagement />
  },
  // Back-entered agreements (islegacy / action='DATA_ENTRY'). A filtered view of
  // dslams_agreement rather than a separate entity — see the page's header comment.
  {
    path: 'data-entry',
    element: <DataEntryManagement />
  },
  // ---- Spec §4.4 screens ---------------------------------------------------
  {
    path: 'transfers',
    element: <TransferManagement />
  },
  {
    path: 'disputes',
    element: <DisputeManagement />
  },
  {
    path: 'calendar',
    element: <LamsViewGate view="dslams_view_calendar"><LeaseCalendar /></LamsViewGate>
  },
  // Masters for the two new setup entities. Declared alongside the transaction
  // screens rather than under 'setup', which is the shared core setup module.
  {
    path: 'agreement-types',
    element: <AgreementTypeManagement />
  },
  {
    path: 'agreement-templates',
    element: <AgreementTemplateManagement />
  },
  {
    path: 'reports',
    element: <ModuleBannerProvider value={LAMS_REPORTS_BANNER}><PassThroughLayout /></ModuleBannerProvider>,
    children: reportsRoutes
  },
  {
    path: 'setup',
    element: <ModuleBannerProvider value={LAMS_SETUP_BANNER}><PassThroughLayout /></ModuleBannerProvider>,
    children: setupRoutes
  }
];

export default leasesAgreementsRoutes;
